import { GetString } from 'fluent-react';
import { rgba } from 'polished';
import React, { RefObject } from 'react';
import styled from 'styled-components';
import {
  CountryProfile,
} from '../../graphQL/types/shared';
import { gql, useQuery } from '../../graphQL/useQuery';
import { SlideElementProps } from '../../presentationManager/PresentationManager';
import { H4 } from '../../sharedComponents/NewTextStyling';
import { GraphControlsContainer } from '../Grid';
import { primaryColor1 } from '../Utils';

export const boxSize = 100; // in px
export const boxLabelSize = 130; // in px
export const gridColumnSpacing = 7; // in px

const Container = styled(GraphControlsContainer)`
  display: grid;
  grid-template-columns: 1fr ${boxSize}px ${boxLabelSize}px;
  grid-column-gap: ${gridColumnSpacing}px;
`;

const Title = styled(H4)`
  grid-column: 1;
  margin: auto 0;

  @media (max-width: 1070px) {
    width: 50%;
    font-size: 1rem;
    margin: 0;
  }

  @media (max-width: 925px) {
    font-size: 0.8rem;
    width: 45%;
  }

  @media (max-width: 850px) {
    font-size: 0.7rem;
    white-space: nowrap;
    margin: 0;
    position: relative;
    top: -9px;
  }
`;

const BoxContainer = styled.div`
  height: 100%;
`;

const BoxBase = styled.div`
  border: 1px solid ${primaryColor1};
`;

const OuterBox = styled(BoxBase)`
  grid-column: 2;
  height: 100%;
  display: flex;
  align-items: flex-end;
  justify-content: flex-end;
  flex-direction: column;
  background-color: ${rgba(primaryColor1, 0.07)};
  position: relative;
`;

const InnerBox = styled(BoxBase)`
  background-color: ${primaryColor1};
`;

const fontSize = '0.7rem';

const Value = styled.div`
  font-size: ${fontSize};
  margin: 2px;
  color: ${primaryColor1};
`;

const TextContainer = styled.div`
  grid-column: 3;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
`;

const Label = styled.div`
  color: #666;
  font-size: ${fontSize};
  display: flex;
  align-items: center;
`;

const Arrow = styled.div`
  margin-right: 3px;
`;

const ProportionSvgContainer = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  width: 100%;
  height: 100%;
  overflow: hidden;

  svg {
    line {
      fill: none;
      stroke: ${primaryColor1};
      stroke-miterlimit: 10;
      stroke-width: 0.4px;
      opacity: 0.85;
    }
  }
`;

const query = gql`
  query DiversificationIntoNewProductsStatus($id: ID!) {
    location: countryProfile(location: $id) {
      exportValue
      newProductExportValue
    }
  }
`;

interface SuccessResponse {
  location: null | {
    exportValue: CountryProfile['exportValue'],
    newProductExportValue: CountryProfile['newProductExportValue'],
  };
}

interface Variables {
  id: string;
}

interface Props extends SlideElementProps {
  countryId: string;
  year: number;
  getFluentString: GetString;
  zIndex: number;
}

const StatusBar = React.forwardRef((props: Props, rootElRef: RefObject<HTMLDivElement>) => {
  const { countryId, year } = props;
  const variables: Variables = {
    id: countryId,
  };
  const {loading, error, data} = useQuery<SuccessResponse, Variables>(query, {variables});
  const {getFluentString, zIndex} = props;

  let output: React.ReactElement<any> | null;
  if (loading === true) {
    output = null;
  } else if (error !== undefined) {
    console.error(error);
    output = null;
  } else if (data !== undefined) {
    const {location} = data;
    if (location === null) {
      output = null;
    } else {
      const {
        exportValue, newProductExportValue,
      } = location;
      if (newProductExportValue !== null && exportValue !== null) {
        const minPercent = 0.01;
        const exportDecimal = newProductExportValue / exportValue;
        const roundedPercent = exportDecimal < minPercent ? '<1' : Math.round(exportDecimal * 100);
        const boxDimensions = exportDecimal < minPercent
              ? Math.sqrt(minPercent) * 100 : Math.sqrt(exportDecimal) * 100;
        const innerBoxDimensions: React.CSSProperties = {
          height: boxDimensions + '%',
          width: boxDimensions + '%',
        };
        const label1 = getFluentString('diversification-into-new-products-percent-label-1', {
          year: `${year}`,
        });
        const label2 = getFluentString('diversification-into-new-products-percent-label-2', {
          'callback-year': `${year - 15}`,
          'year': `${year}`,
        });
        output = (
          <>
            <Title>
              {getFluentString('diversification-into-new-products-chart-title', {
                'callback-year': `${year - 15}`,
                'year': `${year}`,
              })}
            </Title>
            <BoxContainer>
              <OuterBox>
                <Value>
                  <ProportionSvgContainer
                    dangerouslySetInnerHTML={{__html: require('./proportion.svg')}}
                  />
                  {roundedPercent}%
                </Value>
                <InnerBox style={innerBoxDimensions} />
              </OuterBox>
            </BoxContainer>
            <TextContainer>
              <Label>
                <Arrow>→</Arrow>
                <div>{label1}</div>
              </Label>
              <Label>
                <Arrow>→</Arrow>
                <div dangerouslySetInnerHTML={{__html: label2}} />
              </Label>
            </TextContainer>
          </>
        );
      } else {
        output = null;
      }
    }
  } else {
    output = null;
  }
  const style: React.CSSProperties = {
    zIndex,
  };
  return (
    <Container style={style} ref={rootElRef}>
      {output}
    </Container>
  );
});

export default StatusBar;
